const positionKey = '__suiteHistoryPosition'
let activeTracker = null

function storedPosition(state) {
  const marker = state?.[positionKey]
  return typeof marker?.scope === 'string' &&
    marker.scope !== '' &&
    Number.isSafeInteger(marker.position) &&
    marker.position >= 0
    ? marker
    : null
}

function supportsMarker(state) {
  return (
    state === null ||
    state === undefined ||
    Object.prototype.toString.call(state) === '[object Object]'
  )
}

export function readHistoryPosition(state = window.history.state) {
  const marker = storedPosition(state)
  return activeTracker !== null && marker?.scope === activeTracker.scope
    ? marker.position
    : null
}

export function registerHistoryPositionAppCleanup(app, tracker) {
  // Child teardown must restore its host wrappers before app disposal.
  app.onUnmount(() => queueMicrotask(() => tracker.dispose()))
}

/** Keep Suite entry positions through Inertia's native page and scroll writes. */
export function installHistoryPositionTracker() {
  if (activeTracker !== null) return activeTracker.handle

  const history = window.history
  const pushState = history.pushState
  const replaceState = history.replaceState
  const existing = storedPosition(history.state)
  const tracker = {
    scope: existing?.scope ?? window.crypto.randomUUID(),
    position: existing?.position ?? 0,
    disposed: false,
    handle: null,
  }

  function markedState(state, scope, position) {
    return { ...state, [positionKey]: { scope, position } }
  }

  function write(original, owner, args, push) {
    if (tracker.disposed || args.length < 2) return original.apply(owner, args)
    if (!supportsMarker(args[0])) {
      const result = original.apply(owner, args)
      tracker.position = null
      return result
    }
    const scope =
      tracker.position === null ? window.crypto.randomUUID() : tracker.scope
    const position =
      tracker.position === null ? 0 : tracker.position + (push ? 1 : 0)
    const next = [...args]
    next[0] = markedState(args[0], scope, position)
    const result = original.apply(owner, next)
    tracker.scope = scope
    tracker.position = position
    return result
  }

  function trackedPushState(...args) {
    return write(pushState, this, args, true)
  }
  function trackedReplaceState(...args) {
    return write(replaceState, this, args, false)
  }
  function onPopstate(event) {
    tracker.position = readHistoryPosition(event.state)
  }
  function dispose() {
    if (tracker.disposed) return
    tracker.disposed = true
    window.removeEventListener('popstate', onPopstate, true)
    if (history.pushState === trackedPushState) history.pushState = pushState
    if (history.replaceState === trackedReplaceState)
      history.replaceState = replaceState
    if (activeTracker === tracker) activeTracker = null
  }

  if (supportsMarker(history.state)) {
    replaceState.call(
      history,
      markedState(history.state, tracker.scope, tracker.position),
      '',
    )
  } else {
    tracker.position = null
  }
  tracker.handle = { dispose }
  activeTracker = tracker
  history.pushState = trackedPushState
  history.replaceState = trackedReplaceState
  window.addEventListener('popstate', onPopstate, true)
  return tracker.handle
}
